// client/src/coms/components/ChannelPermissionsEditor.js
// Per-channel permission overrides: for "Everyone" or a role, each permission that applies to this kind of
// channel is Deny / Inherit / Allow. Resolution (server, permissions.js): roles -> everyone override ->
// role overrides (an allow on any of the member's roles wins). The server enforces who may change what.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { comsApiGet, comsApiPut } from '../comsApi';
import { useCatalog } from './serversettings_parts/RolesManager';

const STATES = [
  { value: 'deny', label: '✕', title: 'Deny', color: '#da373c' },
  { value: 'inherit', label: '/', title: 'Inherit from roles', color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.25 * var(--vx-ov, 1)))' },
  { value: 'allow', label: '✓', title: 'Allow', color: '#3ba55d' },
];

function TriState({ value, onChange, disabled, label }) {
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))', flexShrink: 0, opacity: disabled ? 0.45 : 1 }}>
      {STATES.map((s) => {
        const active = value === s.value;
        return (
          <button
            key={s.value}
            type="button"
            role="radio"
            aria-checked={active}
            title={s.title}
            disabled={disabled}
            onClick={() => onChange(s.value)}
            style={{ width: 30, height: 26, border: 'none', background: active ? s.color : 'transparent', color: 'var(--vx-text, #fff)', fontWeight: 900, cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

// endpoint (optional): another overrides resource with the same shape, e.g. a category's
// (/servers/:key/categories/:id/overrides).
export default function ChannelPermissionsEditor({ kind, channelId, endpoint = '' }) {
  const { catalog } = useCatalog();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [roleId, setRoleId] = useState(0);
  const [pending, setPending] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  const k = kind === 'voice' ? 'voice' : 'text';
  const base = endpoint || `/channels/${k}/${channelId}/overrides`;

  const load = useCallback(async () => {
    setErr('');
    try {
      setData(await comsApiGet(base));
    } catch (e) {
      setErr(e && e.status === 403 ? 'You need the Manage roles permission to change channel permissions.' : String((e && e.data && e.data.error) || 'Could not load channel permissions.'));
    }
  }, [k, channelId, base]);

  useEffect(() => {
    if (channelId) load();
  }, [channelId, load]);

  const current = useMemo(() => {
    const m = {};
    for (const o of (data && data.overrides) || []) if (o.role_id === roleId) m[o.permission_key] = o.value;
    return m;
  }, [data, roleId]);
  const grantable = useMemo(() => new Set((data && data.grantable) || []), [data]);
  const role = ((data && data.roles) || []).find((r) => r.id === roleId);
  const dirty = Object.keys(pending).length > 0;

  async function save() {
    setBusy(true);
    setErr('');
    try {
      setData(await comsApiPut(`${base}/${roleId}`, { values: pending }));
      setPending({});
      setSaved('Saved. Applies straight away, including in active voice calls.');
      setTimeout(() => setSaved(''), 3500);
    } catch (e) {
      setErr(String((e && e.data && e.data.error) || 'Could not save channel permissions.'));
    } finally {
      setBusy(false);
    }
  }

  if (err && !data) return <div style={{ fontSize: 13, opacity: 0.8 }}>{err}</div>;
  if (!data || !catalog) return <div style={{ fontSize: 13, opacity: 0.7 }}>Loading…</div>;

  const applicable = new Set(data.applicable || []);
  const counts = {};
  for (const o of data.overrides || []) counts[o.role_id] = (counts[o.role_id] || 0) + 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.4 }}>
        Change what roles may do in this channel. “/” keeps the role’s normal permission. An allow on any of a member’s roles beats a deny; the owner is never restricted.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {(data.roles || []).map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => {
              if (dirty) return;
              setRoleId(r.id);
            }}
            disabled={dirty && r.id !== roleId}
            title={dirty && r.id !== roleId ? 'Save or reset first' : undefined}
            style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))', background: r.id === roleId ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.35)' : 'transparent', color: 'var(--vx-on-accent, #fff)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
          >
            {r.color ? <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: r.color, marginRight: 6 }} /> : null}
            {r.name}
            {counts[r.id] ? <span style={{ marginLeft: 6, opacity: 0.7 }}>{counts[r.id]}</span> : null}
          </button>
        ))}
      </div>
      {role && !role.can_manage ? <div style={{ fontSize: 12, opacity: 0.75 }}>This role is not below your highest role, so you cannot change its channel permissions.</div> : null}
      <div style={{ maxHeight: 300, overflowY: 'auto', paddingRight: 4 }}>
        {catalog.groups.map((g) => {
          const items = catalog.permissions.filter((p) => p.group === g.key && applicable.has(p.key));
          if (!items.length) return null;
          return (
            <div key={g.key} style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 0.4, opacity: 0.6, textTransform: 'uppercase', margin: '6px 0 2px' }}>{g.label}</div>
              {items.map((p) => {
                const value = pending[p.key] || current[p.key] || 'inherit';
                const locked = !role || !role.can_manage || !grantable.has(p.key);
                return (
                  <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }} title={locked && role && role.can_manage ? 'You can only change permissions you have yourself' : undefined}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{p.label}</div>
                      <div style={{ fontSize: 11, opacity: 0.6 }}>{p.description}</div>
                    </div>
                    <TriState
                      value={value}
                      disabled={locked || busy}
                      label={p.label}
                      onChange={(v) => {
                        const base = current[p.key] || 'inherit';
                        const next = { ...pending };
                        if (v === base) delete next[p.key];
                        else next[p.key] = v;
                        setPending(next);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {err ? <div className="coms-form-error">{err}</div> : null}
      {saved ? <div style={{ fontSize: 12, color: '#3ba55d' }}>{saved}</div> : null}
      {dirty ? (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="coms-mini-btn" disabled={busy} onClick={() => setPending({})}>
            Reset
          </button>
          <button type="button" className="coms-mini-btn" disabled={busy} onClick={save} style={{ background: '#3ba55d', color: '#fff' }}>
            {busy ? 'Saving…' : 'Save channel permissions'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
